<template>
  <div class="outreach-analytics" style="padding: 1.5rem;">

    <!-- Loading -->
    <div v-if="loading" class="tw-flex items-center justify-center" style="padding: 2rem;">
      <LoadingNew/>
    </div>

    <template v-else>
      <!-- Error -->
      <div v-if="error" class="notice notice--error" style="margin-bottom: 1.5rem;">
        {{ error }}
      </div>

      <!-- Summary cards -->
      <div v-if="summary.length > 0" style="margin-bottom: 1.5rem;">
        <h4 style="margin-bottom: 0.75rem;">Quick summary</h4>
        <div class="tw-flex flex-wrap gap-3">
          <div
            v-for="card in summary"
            :key="card.template_id"
            style="background: #f8f8f8; border-radius: 4px; padding: 0.75rem 1rem; min-width: 200px;"
          >
            <div style="font-weight: 600; font-size: 0.8125rem; color: #555; margin-bottom: 0.5rem;">
              {{ card.template_name || card.template_id }}
              <span v-if="card.template_name" style="font-weight: 400; color: #888; font-family: monospace;">({{ card.template_id }})</span>
            </div>
            <div class="tw-flex gap-4">
              <div style="text-align: center;">
                <div style="font-size: 1.25rem; font-weight: 700;">{{ card.total_sent }}</div>
                <div style="font-size: 0.75rem; color: #888;">sent</div>
              </div>
              <div style="text-align: center;">
                <div style="font-size: 1.25rem; font-weight: 700; color: #2d9e5c;">{{ card.consumed }}</div>
                <div style="font-size: 0.75rem; color: #888;">accepted</div>
              </div>
              <div style="text-align: center;">
                <div style="font-size: 1.25rem; font-weight: 700; color: #c0392b;">{{ card.expired }}</div>
                <div style="font-size: 0.75rem; color: #888;">expired</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="records.length === 0" style="padding: 2rem; text-align: center; color: #888;">
        No outreach records found. Send invitations from the List tab to see analytics here.
      </div>

      <template v-else>
        <!-- Records table -->
        <table class="table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Company</th>
              <th>Sent at</th>
              <th>Template</th>
              <th>Delivery status</th>
              <th>Invitation status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="record in records" :key="record.id">
              <td>{{ record.email }}</td>
              <td>{{ record.company_name || '—' }}</td>
              <td>{{ formatDate(record.outreach_at) }}</td>
              <td style="font-size: 0.8125rem; color: #555;">
                <template v-if="record.email_template_id">
                  {{ record.email_template_name || record.email_template_id }}
                  <span v-if="record.email_template_name" style="color: #aaa; font-family: monospace;">({{ record.email_template_id }})</span>
                </template>
                <template v-else>—</template>
              </td>
              <td>{{ record.delivery_status || '—' }}</td>
              <td>
                <span :class="statusBadgeClass(record.invitation_status)" style="display: inline-block; padding: 0.15rem 0.5rem; border-radius: 3px; font-size: 0.75rem; font-weight: 600;">
                  {{ record.invitation_status }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <pagination
          class="simple-pagination--bottom"
          :paging="paging"
          @change="setOffset"
        />
      </template>
    </template>
  </div>
</template>

<script>
  import { fetchAnalytics } from '../../../api/outreach'
  import { defineComponent } from 'vue'
  import Pagination from '../../Pagination/SimplePagination.vue'
  import LoadingNew from '@/components/Loaders/LoadingNew.vue'

  export default defineComponent({
    name: 'outreach-analytics',
    components: { LoadingNew, Pagination },
    data () {
      return {
        records: [],
        summary: [],
        paging: {},
        loading: false,
        error: null,
        offset: 0,
      }
    },
    mounted () {
      this.fetchList()
    },
    methods: {
      fetchList () {
        this.loading = true
        this.error = null
        fetchAnalytics({ limit: 50, offset: this.offset })
          .then((response) => {
            this.records = response.data?.data ?? []
            this.summary = response.data?.summary ?? []
            this.paging = response.paging
          })
          .catch(() => {
            this.error = 'Failed to load analytics. Please try again.'
          })
          .finally(() => {
            this.loading = false
          })
      },
      setOffset (offset) {
        this.offset = offset
        this.fetchList()
      },
      formatDate (value) {
        if (!value) return '—'
        const d = new Date(value)
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
      },
      statusBadgeClass (status) {
        switch (status) {
          case 'consumed':
          case 'registered':
            return 'outreach-analytics__badge--green'
          case 'expired':
            return 'outreach-analytics__badge--red'
          case 'pending':
          default:
            return 'outreach-analytics__badge--amber'
        }
      },
    },
  })
</script>

<style scoped>
  .outreach-analytics__badge--green {
    background: #d4f5e2;
    color: #1a6e3c;
  }

  .outreach-analytics__badge--amber {
    background: #fff3cd;
    color: #856404;
  }

  .outreach-analytics__badge--red {
    background: #fde8e8;
    color: #9b1c1c;
  }
</style>
